import type { ReactNode } from 'react';
import { FaCopy, FaExternalLinkAlt, FaRobot } from 'react-icons/fa';
import { Badge, Button } from '@/components/UI';
import ObjectiveModeControl, {
    type ObjectiveMode,
    type RealizedOutcomeMaterializationEntityType
} from '../parameterDiscovery/ObjectiveModeControl';
import McpReadinessPanel from './McpReadinessPanel';
import AIWorkApprovalRow from '@/components/AIWorkbench/AIWorkApprovalRow';
import CancelScorecardSessionButton from './CancelScorecardSessionButton';
import PromptModalButton from './PromptModalButton';
import ScorecardAuditCoveragePanel from './ScorecardAuditCoveragePanel';
import ScorecardGenerationToolControl from './ScorecardGenerationToolControl';
import ScorecardMetricControl from './ScorecardMetricControl';
import ScorecardSessionProgress from './ScorecardSessionProgress';
import { scorecardGenerationScopeOptions, toolLaunchUrls } from './constants';
import {
    formatCount,
    formatTimestamp,
    getScorecardGenerationStatusBadgeVariant,
    getWorkbenchPanelReadiness,
    isScorecardGenerationSessionTerminal
} from './helpers';
import type {
    McpClientReadiness,
    ParameterDiscoveryMetric,
    ScorecardGenerationCandidateScope,
    ScorecardGenerationDraft,
    ScorecardGenerationExternalTool,
    ScorecardGenerationSessionDetails,
    ScorecardGenerationSessionList,
    ScorecardGenerationSessionSummary
} from './types';

const ControlField = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs text-zinc-400">{label}</span>
        {children}
    </div>
);

const ScorecardGenerationScopeControl = ({
    value,
    disabled,
    onChange
}: {
    value: ScorecardGenerationCandidateScope;
    disabled: boolean;
    onChange: (value: ScorecardGenerationCandidateScope) => void;
}) => (
    <select
        className="rounded border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm text-white"
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value as ScorecardGenerationCandidateScope)}
    >
        {scorecardGenerationScopeOptions.map((option) => (
            <option key={option.value} value={option.value}>
                {option.label}
            </option>
        ))}
    </select>
);

const ScorecardGenerationSessionRow = ({
    session,
    selected,
    onSelect
}: {
    session: ScorecardGenerationSessionSummary;
    selected: boolean;
    onSelect: (sessionId: number) => void;
}) => (
    <button
        className={`flex w-full flex-col gap-1.5 rounded border px-3 py-2 text-left text-sm transition-colors ${
            selected ? 'border-blue-700 bg-blue-950/20' : 'border-zinc-800 bg-zinc-950/40 hover:border-zinc-700'
        }`}
        type="button"
        onClick={() => onSelect(session.id)}
    >
        <div className="flex items-start justify-between gap-2">
            <div
                className="line-clamp-2 min-w-0 font-medium text-zinc-200"
                title={`${session.externalToolLabel ?? session.externalTool} · ${session.candidateScope}`}
            >
                {session.externalToolLabel ?? session.externalTool} · {session.candidateScope}
            </div>
            <Badge className="shrink-0" variant={getScorecardGenerationStatusBadgeVariant(session.status)}>
                {session.status.replaceAll('_', ' ')}
            </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span>#{session.id}</span>
            <Badge variant="secondary">{session.metric === 'options' ? 'options' : 'stock'}</Badge>
            <Badge variant={session.objectiveMode === 'strategy_exit' ? 'success' : 'secondary'}>
                {session.objectiveMode === 'strategy_exit' ? `realized #${session.realizedOutcomeSetId ?? 'none'}` : 'fixed horizon'}
            </Badge>
            <span className="ml-auto text-zinc-400">
                {formatCount(session.reviewedFeatureCount)}/{formatCount(session.featureCount)}
            </span>
        </div>
    </button>
);

const ScorecardGenerationDraftRow = ({
    draft,
    recommended,
    canCreate,
    disabled,
    isCreating,
    createdStrategyVariantId,
    onCreate
}: {
    draft: ScorecardGenerationDraft;
    recommended: boolean;
    canCreate: boolean;
    disabled: boolean;
    isCreating: boolean;
    createdStrategyVariantId: number | null;
    onCreate: (draftId: number) => void;
}) => (
    <div className="grid gap-2 border-t border-zinc-800 py-3 first:border-t-0 md:grid-cols-[1fr_110px_auto] md:items-center">
        <div className="min-w-0">
            <div className="truncate text-sm font-medium text-white" title={draft.name}>
                {draft.name}
            </div>
            <div className="truncate text-xs text-zinc-500" title={draft.summary}>
                {draft.summary}
            </div>
        </div>
        <Badge variant={recommended ? 'success' : 'secondary'}>{recommended ? 'recommended' : draft.status}</Badge>
        {draft.createdScorecardId ? (
            <Button
                size="sm"
                to={
                    createdStrategyVariantId
                        ? `/user/scorecard/${draft.createdScorecardId}?strategyId=${createdStrategyVariantId}`
                        : `/user/scorecard/${draft.createdScorecardId}`
                }
                variant="secondary"
            >
                Open Scorecard
            </Button>
        ) : (
            <Button
                disabled={disabled || !recommended || !canCreate}
                isLoading={isCreating}
                size="sm"
                variant="secondary"
                onClick={() => onCreate(draft.id)}
            >
                {canCreate ? 'Create Scorecard' : 'Approve First'}
            </Button>
        )}
    </div>
);

interface ScorecardGenerationSectionProps {
    experimentId: number;
    duration: string;
    horizonSeconds: number;
    scorecardMetric: ParameterDiscoveryMetric;
    hasOptionMetricData: boolean | undefined;
    objectiveMode: ObjectiveMode;
    objectiveEvidenceReady: boolean;
    realizedOutcomeSetId: number | null;
    materializationEntityType?: RealizedOutcomeMaterializationEntityType | null;
    strategyId?: number | null;
    onObjectiveModeChange: (mode: ObjectiveMode) => void;
    onRealizedOutcomeSetIdChange: (id: number | null) => void;
    tool: ScorecardGenerationExternalTool;
    candidateScope: ScorecardGenerationCandidateScope;
    readiness: McpClientReadiness | undefined;
    sessions: ScorecardGenerationSessionList | undefined;
    selectedSessionId: number | null;
    details: ScorecardGenerationSessionDetails | undefined;
    isLoadingSessions: boolean;
    isLoadingDetails: boolean;
    errorMessage: string | undefined;
    actionPending: boolean;
    isStarting: boolean;
    isCreatingScorecard: boolean;
    isDecidingApproval: boolean;
    isCancellingSession: boolean;
    onScorecardMetricChange: (value: ParameterDiscoveryMetric) => void;
    onToolChange: (value: ScorecardGenerationExternalTool) => void;
    onScopeChange: (value: ScorecardGenerationCandidateScope) => void;
    onStart: () => void;
    onOpenExternalTool: () => void;
    onSelectSession: (sessionId: number) => void;
    onCopyPrompt: () => void;
    onCreateScorecard: (draftId: number) => void;
    onDecideApproval: (approvalId: number, status: 'approved' | 'rejected') => void;
    onCancelSession: (sessionId: number, reason: string, onCancelled: () => void) => void;
}

const ScorecardGenerationSection = ({
    experimentId,
    duration,
    horizonSeconds,
    scorecardMetric,
    hasOptionMetricData,
    objectiveMode,
    objectiveEvidenceReady,
    realizedOutcomeSetId,
    materializationEntityType = null,
    strategyId = null,
    onObjectiveModeChange,
    onRealizedOutcomeSetIdChange,
    tool,
    candidateScope,
    readiness,
    sessions,
    selectedSessionId,
    details,
    isLoadingSessions,
    isLoadingDetails,
    errorMessage,
    actionPending,
    isStarting,
    isCreatingScorecard,
    isDecidingApproval,
    isCancellingSession,
    onScorecardMetricChange,
    onToolChange,
    onScopeChange,
    onStart,
    onOpenExternalTool,
    onSelectSession,
    onCopyPrompt,
    onCreateScorecard,
    onDecideApproval,
    onCancelSession
}: ScorecardGenerationSectionProps) => {
    const activeSession = details?.session;
    const drafts = details?.drafts ?? [];
    const approvals = details?.workbench?.approvals ?? [];
    const artifacts = details?.workbench?.artifacts ?? [];

    const panelReadiness = getWorkbenchPanelReadiness(
        details?.workbench?.session,
        details?.workbench?.connectionReadiness,
        tool,
        readiness
    );

    const recommendedDraft =
        activeSession?.recommendedDraftId != null
            ? drafts.find((draft) => draft.id === activeSession.recommendedDraftId)
            : drafts.find((draft) => draft.status === 'recommended');
    const walkForwardEvaluation = details?.walkForwardEvaluation ?? null;
    const evaluatedDraft = drafts.find((draft) => draft.id === walkForwardEvaluation?.draftId);
    const finalization = details?.finalization ?? null;
    const recommendedDraftIsReady = recommendedDraft?.status === 'recommended' || recommendedDraft?.status === 'scorecard_created';

    const realizedOutcomeSetMissing = objectiveMode === 'strategy_exit' && realizedOutcomeSetId === null;

    const approvedDraftIds = new Set<number>();

    for (const artifact of artifacts) {
        if (
            artifact.artifactType !== 'scorecard_draft' ||
            artifact.sourceTable !== 'scorecard_generation_drafts' ||
            typeof artifact.sourceId !== 'number'
        ) {
            continue;
        }

        const isApproved = approvals.some(
            (approval) =>
                approval.artifactId === artifact.id &&
                approval.approvalType === 'create_scorecard' &&
                (approval.status === 'approved' || approval.status === 'executed')
        );

        if (isApproved) {
            approvedDraftIds.add(artifact.sourceId);
        }
    }

    return (
        <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3">
            <div className="mb-3">
                <div className="font-medium text-white">Generate Scorecard with AI</div>
                <div className="mt-1 text-xs text-zinc-500">
                    Launch a {scorecardMetric === 'options' ? 'options' : 'stock'} scorecard-generation task for an external MCP agent
                </div>
            </div>

            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
                <ControlField label="External Tool">
                    <ScorecardGenerationToolControl disabled={actionPending} value={tool} onChange={onToolChange} />
                </ControlField>
                <ScorecardMetricControl
                    disabled={actionPending}
                    hasOptionMetricData={hasOptionMetricData}
                    value={scorecardMetric}
                    onChange={onScorecardMetricChange}
                />
                <ControlField label="Candidate Scope">
                    <ScorecardGenerationScopeControl disabled={actionPending} value={candidateScope} onChange={onScopeChange} />
                </ControlField>
                <ObjectiveModeControl
                    allowPrepareDiscovery
                    requireOutcomeSet
                    disabled={actionPending}
                    duration={duration}
                    experimentId={experimentId}
                    horizonSeconds={horizonSeconds}
                    materializationEntityType={materializationEntityType}
                    objectiveMode={objectiveMode}
                    realizedOutcomeSetId={realizedOutcomeSetId}
                    selectId="scorecard-generation-realized-outcome-set"
                    strategyId={strategyId}
                    onObjectiveModeChange={onObjectiveModeChange}
                    onRealizedOutcomeSetIdChange={onRealizedOutcomeSetIdChange}
                />
                <Button
                    className="inline-flex items-center lg:ml-auto"
                    disabled={actionPending || realizedOutcomeSetMissing || !objectiveEvidenceReady}
                    icon={<FaRobot />}
                    isLoading={isStarting}
                    size="sm"
                    variant="primary"
                    onClick={onStart}
                >
                    Generate {scorecardMetric === 'options' ? 'Options' : 'Stock'} Scorecard with AI
                </Button>
            </div>

            {objectiveMode === 'strategy_exit' && realizedOutcomeSetId !== null && !objectiveEvidenceReady && (
                <div className="mb-4 rounded border border-amber-800/70 bg-amber-950/20 p-3 text-sm text-amber-200">
                    This outcome set is materialized, but its realized parameter discovery is not ready. Choose Prepare discovery above;
                    scorecard generation will remain disabled until the evidence is ready.
                </div>
            )}

            <div className="mb-4">
                <McpReadinessPanel readiness={panelReadiness} tool={tool} onOpenExternalTool={onOpenExternalTool} />
            </div>

            {errorMessage && (
                <div className="mb-3 rounded border border-red-900/70 bg-red-950/20 p-3 text-sm text-red-300">{errorMessage}</div>
            )}

            <div className="grid gap-4 lg:grid-cols-[minmax(260px,0.8fr)_minmax(320px,1.2fr)]">
                <div>
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Sessions</div>
                        {isLoadingSessions && <div className="text-xs text-zinc-500">Loading...</div>}
                    </div>
                    <div className="space-y-2">
                        {sessions && sessions.length > 0 ? (
                            sessions.map((session) => (
                                <ScorecardGenerationSessionRow
                                    key={session.id}
                                    selected={(selectedSessionId ?? sessions[0]?.id) === session.id}
                                    session={session}
                                    onSelect={onSelectSession}
                                />
                            ))
                        ) : (
                            <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3 text-sm text-zinc-500">No sessions yet.</div>
                        )}
                    </div>
                </div>

                <div className="space-y-3">
                    {isLoadingDetails ? (
                        <div className="text-sm text-zinc-500">Loading session...</div>
                    ) : activeSession ? (
                        <>
                            <ScorecardSessionProgress
                                claimedFeatureCount={activeSession.claimedFeatureCount}
                                draftCount={activeSession.draftCount}
                                featureCount={activeSession.featureCount}
                                reviewedFeatureCount={activeSession.reviewedFeatureCount}
                            />

                            {details?.externalAuditCoverage && <ScorecardAuditCoveragePanel coverage={details.externalAuditCoverage} />}

                            {details?.workbench?.session && (
                                <div className="rounded border border-zinc-800 bg-zinc-900 p-3 text-xs leading-5 text-zinc-400">
                                    <div className="mb-1 flex flex-wrap items-center gap-2">
                                        <span className="font-medium text-zinc-300">AI Workbench</span>
                                        <Badge variant={getScorecardGenerationStatusBadgeVariant(details.workbench.session.status)}>
                                            {details.workbench.session.status.replaceAll('_', ' ')}
                                        </Badge>
                                        {!isScorecardGenerationSessionTerminal(activeSession.status) && (
                                            <div className="ml-auto">
                                                <CancelScorecardSessionButton
                                                    disabled={actionPending}
                                                    isCancelling={isCancellingSession}
                                                    sessionId={activeSession.id}
                                                    onCancel={onCancelSession}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {details.workbench.session.firstAgentSeenAt ? (
                                        <span>
                                            External agent first connected at {formatTimestamp(details.workbench.session.firstAgentSeenAt)}.
                                        </span>
                                    ) : (
                                        <span>Waiting for an external MCP agent to use the prompt and call Project314 tools.</span>
                                    )}
                                </div>
                            )}

                            {activeSession.status === 'cancelled' && activeSession.lastError && (
                                <div className="rounded border border-red-500/30 bg-red-500/10 p-3 text-xs leading-5 text-red-200">
                                    {activeSession.lastError}
                                </div>
                            )}

                            {activeSession.status === 'scorecard_created' && finalization && (
                                <div
                                    className={`rounded border p-3 text-xs leading-5 ${
                                        finalization.status === 'attached'
                                            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                                            : finalization.status === 'blocked'
                                              ? 'border-red-500/30 bg-red-500/10 text-red-200'
                                              : 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                                    }`}
                                >
                                    <div className="font-medium">Scorecard finalization: {finalization.status.replaceAll('_', ' ')}</div>
                                    <div>{finalization.message}</div>
                                    {finalization.status === 'attached' && (
                                        <div>No backtests were queued, and the strategy variant remains inactive.</div>
                                    )}
                                </div>
                            )}

                            {walkForwardEvaluation && (
                                <div
                                    className={`rounded border p-3 text-xs leading-5 ${
                                        walkForwardEvaluation.status === 'ready' && walkForwardEvaluation.warningCount === 0
                                            ? 'border-zinc-800 bg-zinc-900 text-zinc-300'
                                            : walkForwardEvaluation.status === 'failed'
                                              ? 'border-red-500/30 bg-red-500/10 text-red-200'
                                              : 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                                    }`}
                                >
                                    <div className="font-medium">
                                        Walk-forward {walkForwardEvaluation.status.replaceAll('_', ' ')} for committed draft #
                                        {walkForwardEvaluation.draftId}
                                    </div>
                                    {walkForwardEvaluation.status === 'ready' ? (
                                        <div>
                                            {walkForwardEvaluation.windowStartDateTime} → {walkForwardEvaluation.windowEndDateTime}:{' '}
                                            {walkForwardEvaluation.acceptedCount?.toLocaleString() ?? '0'} accepted trades,{' '}
                                            {walkForwardEvaluation.averageOutcome?.toFixed(2) ?? 'n/a'}% average outcome. Evaluated gate:{' '}
                                            predicted profit ≥ {walkForwardEvaluation.minPredictedProfit?.toFixed(2) ?? 'n/a'}%, matched
                                            rules ≥ {walkForwardEvaluation.minMatchedRules ?? 'n/a'}, positive rules ≥{' '}
                                            {walkForwardEvaluation.minPositiveMatchedRules ?? 'n/a'}.
                                        </div>
                                    ) : walkForwardEvaluation.status === 'failed' ? (
                                        <div>
                                            {walkForwardEvaluation.errorMessage ?? 'The walk-forward evaluation could not complete.'}{' '}
                                            Resolve missing or broken evaluation data before creating the research variant. Weak performance
                                            from a completed evaluation does not by itself prevent creation.
                                        </div>
                                    ) : (
                                        <div>The walk-forward evaluation is still pending for this draft.</div>
                                    )}
                                    {walkForwardEvaluation.assessment && (
                                        <div className="mt-2">
                                            <div className="font-medium">
                                                {walkForwardEvaluation.assessment.status === 'insufficient_evidence'
                                                    ? 'Insufficient evidence'
                                                    : walkForwardEvaluation.assessment.status === 'fail'
                                                      ? 'Economic assessment failed'
                                                      : 'Economic assessment passed'}
                                            </div>
                                            <div>{walkForwardEvaluation.assessment.reasons.join(' ')}</div>
                                            {walkForwardEvaluation.assessment.status === 'insufficient_evidence' && (
                                                <div>{walkForwardEvaluation.assessment.economicReasons.join(' ')}</div>
                                            )}
                                        </div>
                                    )}
                                    {walkForwardEvaluation.status === 'ready' && walkForwardEvaluation.simulationCurrent && (
                                        <div className="mt-2">
                                            Evaluation completed. Creating a research variant keeps it inactive for backtesting.
                                        </div>
                                    )}
                                    {walkForwardEvaluation.status === 'ready' && !walkForwardEvaluation.simulationCurrent && (
                                        <div className="mt-2 text-amber-200">
                                            This result came from an older version of the walk-forward test and is out of date. It is
                                            re-evaluated with the same committed gate before the draft can be approved or created.
                                        </div>
                                    )}
                                    {walkForwardEvaluation.warningCount > 0 && (
                                        <div className="mt-2">{walkForwardEvaluation.warningCount} evaluation warning(s).</div>
                                    )}
                                    {evaluatedDraft?.validationSummary && (
                                        <div className="mt-2 whitespace-pre-line">{evaluatedDraft.validationSummary}</div>
                                    )}
                                </div>
                            )}

                            {approvals.length > 0 && (
                                <div>
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Approval Requests</div>
                                        <Badge variant="secondary">{approvals.length}</Badge>
                                    </div>
                                    <div>
                                        {approvals.map((approval) => (
                                            <AIWorkApprovalRow
                                                approval={approval}
                                                disabled={actionPending}
                                                isDeciding={isDecidingApproval}
                                                key={approval.id}
                                                onDecide={onDecideApproval}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="mb-2 flex items-center justify-between gap-2">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400">External Agent Prompt</div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button icon={<FaCopy />} size="sm" variant="secondary" onClick={onCopyPrompt}>
                                            Copy
                                        </Button>
                                        <PromptModalButton prompt={details?.agentPrompt ?? ''} title="Scorecard Generation Prompt" />
                                        {toolLaunchUrls[tool] && (
                                            <Button icon={<FaExternalLinkAlt />} size="sm" variant="secondary" onClick={onOpenExternalTool}>
                                                Open Tool
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="mb-2 flex items-center justify-between gap-2">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Drafts</div>
                                    {recommendedDraft && (
                                        <Badge variant={recommendedDraftIsReady ? 'success' : 'warning'}>
                                            {recommendedDraftIsReady ? 'Recommended' : 'Committed'} #{recommendedDraft.id}
                                        </Badge>
                                    )}
                                </div>
                                {drafts.length > 0 ? (
                                    <div>
                                        {drafts.map((draft) => (
                                            <ScorecardGenerationDraftRow
                                                canCreate={approvedDraftIds.has(draft.id)}
                                                disabled={actionPending}
                                                draft={draft}
                                                createdStrategyVariantId={activeSession.createdStrategyVariantId}
                                                isCreating={isCreatingScorecard}
                                                key={draft.id}
                                                recommended={recommendedDraft?.id === draft.id && recommendedDraftIsReady}
                                                onCreate={onCreateScorecard}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3 text-sm text-zinc-500">
                                        No AI drafts submitted yet.
                                    </div>
                                )}
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default ScorecardGenerationSection;
